import React, { useState } from 'react';
import { Box, Typography, Chip, Avatar } from '@mui/material';
import {
  Chat as ChatIcon,
  Person as PersonIcon,
  SmartToy as AiIcon,
  SupervisorAccount as TherapistIcon
} from '@mui/icons-material';

const ROLE_CONFIG = {
  client:          { label: 'Client',       align: 'left',   bgcolor: '#e3f2fd', icon: PersonIcon },
  ai:              { label: 'AI Therapist', align: 'right',  bgcolor: '#f3e5f5', icon: AiIcon },
  human_therapist: { label: 'Therapist',    align: 'right',  bgcolor: '#e8f5e9', icon: TherapistIcon },
  system:          { label: 'System',       align: 'center', bgcolor: '#fff3e0', icon: null }
};

function MessageBubble({ msg }) {
  const config = ROLE_CONFIG[msg.senderRole] || ROLE_CONFIG.client;
  const isCenter = config.align === 'center';
  const isRight = config.align === 'right';
  const IconComponent = config.icon;

  const timeStr = msg.timestamp
    ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  if (isCenter) {
    return (
      <Box sx={{ textAlign: 'center', my: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.65rem' }}>
          {msg.text}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isRight ? 'flex-end' : 'flex-start',
        mb: 0.75
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          maxWidth: '88%',
          flexDirection: isRight ? 'row-reverse' : 'row',
          gap: 0.5
        }}
      >
        {IconComponent && (
          <Avatar sx={{ width: 20, height: 20, bgcolor: config.bgcolor, mt: 0.25 }}>
            <IconComponent sx={{ fontSize: 12, color: 'text.secondary' }} />
          </Avatar>
        )}
        <Box
          sx={{
            bgcolor: config.bgcolor,
            borderRadius: 1.5,
            px: 1.25,
            py: 0.75,
            minWidth: 0
          }}
        >
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.secondary' }}>
              {config.label}
            </Typography>
            {timeStr && (
              <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>
                {timeStr}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.45, wordBreak: 'break-word' }}>
            {msg.text}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function TassConversationSegments({ data }) {
  const [activeSegment, setActiveSegment] = useState(0);

  if (!data || !data.segments || data.segments.length === 0) {
    return null;
  }

  const { segments, clientName, conversationDate, totalMessages, query } = data;
  const segment = segments[activeSegment] || segments[0];

  const dateStr = conversationDate
    ? new Date(conversationDate).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  const shownCount = segment.messages ? segment.messages.length : 0;

  return (
    <Box
      sx={{
        mt: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.75,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <ChatIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
          {clientName}'s conversation
        </Typography>
        {dateStr && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', ml: 'auto' }}>
            {dateStr}
          </Typography>
        )}
      </Box>

      {/* Segment navigation */}
      {segments.length > 1 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1.25, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          {segments.map((seg, idx) => (
            <Chip
              key={idx}
              label={seg.label || `Segment ${idx + 1}`}
              size="small"
              variant={idx === activeSegment ? 'filled' : 'outlined'}
              color={idx === activeSegment ? 'primary' : 'default'}
              onClick={() => setActiveSegment(idx)}
              sx={{ height: 22, fontSize: '0.62rem', cursor: 'pointer' }}
            />
          ))}
        </Box>
      )}

      {/* Match reason */}
      {segment.matchReason && (
        <Box sx={{ px: 1.25, pt: 0.75, pb: 0.25 }}>
          <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.65rem' }}>
            {segment.matchReason}
          </Typography>
        </Box>
      )}

      {/* Messages */}
      <Box
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          px: 1,
          py: 0.75
        }}
      >
        {segment.messages && segment.messages.map((msg, idx) => (
          <MessageBubble key={idx} msg={msg} />
        ))}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 1.25,
          py: 0.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
          Showing {shownCount} of {totalMessages || '?'} messages
        </Typography>
        {query && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', fontStyle: 'italic' }}>
            Search: "{query}"
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default TassConversationSegments;
